import { useState, useEffect } from "react";
import { Modal, Spinner, InputGroup, Form } from "react-bootstrap";
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';
import { spinner } from "../utils/Helper";
import {
    getFirestore, collection, getDocs, doc, setDoc, updateDoc, query, where, orderBy, limit, Timestamp,
    onSnapshot
} from 'firebase/firestore';

import { FaCheck, FaTimesCircle, FaUser, FaKey, FaPlus, FaExclamationTriangle } from 'react-icons/fa';

import check from "../images/check.png";
import expFail from '../images/expFail.png';

export default function AccessVitals(props) {
    const db = getFirestore();
    const [accessItems, setAccessItems] = useState([]);
    const [guestItems, setGuestItems] = useState([]);
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [newGuestName, setNewGuestName] = useState("");
    const [isAddingGuest, setIsAddingGuest] = useState(false);
    const [memberEmail, setMemberEmail] = useState("");

    const getLogsCollectionName = (date = new Date()) => {
        const year = date instanceof Date ? date.getFullYear() : new Date().getFullYear();
        return `access_logs_${year}`;
    };

    const getActiveShiftDateStr = (date = new Date()) => {
        const d = new Date(date);
        if (d.getHours() < 2) {
            d.setDate(d.getDate() - 1);
        }
        return d.toISOString().split('T')[0];
    };

    const getShiftBoundaries = () => {
        const startOfWindow = new Date();
        const endOfWindow = new Date();

        if (startOfWindow.getHours() < 2) {
            startOfWindow.setDate(startOfWindow.getDate() - 1);
            startOfWindow.setHours(2, 0, 0, 0);
            endOfWindow.setHours(2, 0, 0, 0);
        } else {
            startOfWindow.setHours(2, 0, 0, 0);
            endOfWindow.setDate(endOfWindow.getDate() + 1);
            endOfWindow.setHours(2, 0, 0, 0);
        }

        return { startOfWindow, endOfWindow };
    };

    useEffect(() => {
        let email = props.memberEmail;
        if (!email || email === "Login") {
            email = localStorage.getItem('email');
        }

        let unsubscribe = () => { };

        if (email && email !== "Login") {
            setMemberEmail(email);
            unsubscribe = getAccessDoc(email);
        }

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [props.memberEmail, props.refreshTrigger]);

    const fetchGuests = async (email) => {
        if (!email) return [];
        const logsRef = collection(db, getLogsCollectionName());
        const { startOfWindow, endOfWindow } = getShiftBoundaries();

        const q = query(
            logsRef,
            where("signedInBy", "==", email.toLowerCase()),
            where("entryType", "==", "Guest"),
            where("scannedAt", ">=", Timestamp.fromDate(startOfWindow)),
            where("scannedAt", "<=", Timestamp.fromDate(endOfWindow)),
            orderBy("scannedAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(guest => guest.status !== "cancelled" && guest.status !== "removed" && !guest.remove && !guest.noshow);
    };

    const getAccessDoc = (identifier) => {
        if (!identifier) return () => { };
        const logsRef = collection(db, getLogsCollectionName());
        const { startOfWindow, endOfWindow } = getShiftBoundaries();

        try {
            let qMember = query(
                logsRef,
                where("rawPayload", "==", identifier),
                where("scannedAt", ">=", Timestamp.fromDate(startOfWindow)),
                where("scannedAt", "<=", Timestamp.fromDate(endOfWindow)),
                orderBy("scannedAt", "desc"),
                limit(5)
            );

            const unsubscribe = onSnapshot(qMember, async (snapMember) => {
                let docsList = snapMember.docs.map(d => ({ id: d.id, ...d.data() }));

                // Ensure we select the Member's record, not a Guest log matching rawPayload
                let memberDoc = docsList.find(d => d.entryType !== "Guest");

                if (!memberDoc && identifier.toLowerCase() !== identifier) {
                    const qLower = query(
                        logsRef,
                        where("rawPayload", "==", identifier.toLowerCase()),
                        where("scannedAt", ">=", Timestamp.fromDate(startOfWindow)),
                        where("scannedAt", "<=", Timestamp.fromDate(endOfWindow)),
                        orderBy("scannedAt", "desc"),
                        limit(5)
                    );
                    const snapLower = await getDocs(qLower);
                    docsList = snapLower.docs.map(d => ({ id: d.id, ...d.data() }));
                    memberDoc = docsList.find(d => d.entryType !== "Guest");
                }

                const cachedName = localStorage.getItem("user");
                if (!memberDoc && cachedName) {
                    const qCached = query(
                        logsRef,
                        where("rawPayload", "==", cachedName),
                        where("scannedAt", ">=", Timestamp.fromDate(startOfWindow)),
                        where("scannedAt", "<=", Timestamp.fromDate(endOfWindow)),
                        orderBy("scannedAt", "desc"),
                        limit(5)
                    );
                    const snapCached = await getDocs(qCached);
                    docsList = snapCached.docs.map(d => ({ id: d.id, ...d.data() }));
                    memberDoc = docsList.find(d => d.entryType !== "Guest");
                }

                if (memberDoc) {
                    const activeGuests = await fetchGuests(identifier);
                    const hasPendingGuest = activeGuests.some(g => !g.isVerified || g.isFlagged || g.hasIncident);
                    const allGuestsVerified = activeGuests.length > 0 && activeGuests.every(g => g.isVerified && !g.isFlagged && !g.hasIncident);

                    setAccessItems([{
                        ...memberDoc,
                        guestCount: activeGuests.length,
                        hasPendingGuest: hasPendingGuest,
                        allGuestsVerified: allGuestsVerified
                    }]);
                } else {
                    // Fallback stub for member if log is not created yet but they are logged in
                    const activeGuests = await fetchGuests(identifier);
                    setAccessItems([{
                        id: `stub_${identifier}`,
                        rawPayload: identifier,
                        entryType: "Member",
                        status: "active",
                        isVerified: false,
                        isExpired: false,
                        guestCount: activeGuests.length,
                        hasPendingGuest: activeGuests.some(g => !g.isVerified || g.isFlagged || g.hasIncident),
                        allGuestsVerified: activeGuests.length > 0 && activeGuests.every(g => g.isVerified && !g.isFlagged && !g.hasIncident)
                    }]);
                }
            }, (e) => {
                console.error("Query Error:", e);
            });

            return unsubscribe;
        } catch (e) {
            console.error("Query Setup Error:", e);
            return () => { };
        }
    };

    const handleShowGuests = async (email) => {
        if (!email) return;
        spinner(true);
        try {
            const guests = await fetchGuests(email);
            setGuestItems(guests);
            setShowGuestModal(true);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load guests");
        } finally {
            spinner(false);
        }
    };

    const handleAddGuest = async (email) => {
        if (!newGuestName.trim()) return toast.warning("Enter a name");
        setIsAddingGuest(true);
        try {
            const nameParts = newGuestName.trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            const bannedQuery = query(
                collection(db, "banned_list"),
                where("firstName", "==", firstName),
                where("lastName", "==", lastName)
            );
            const bannedSnapshot = await getDocs(bannedQuery);

            if (!bannedSnapshot.empty) {
                toast.error("The person you are trying to add is on a banned list.");
                setIsAddingGuest(false);
                return;
            }

            const timestamp = new Date();
            const shiftDateStr = getActiveShiftDateStr(timestamp);
            const cleanMemberEmail = email.toLowerCase().trim();

            let sponsorFullName = "";
            try {
                const profQuery = query(
                    collection(db, "profiles"),
                    where("email", "==", cleanMemberEmail),
                    limit(1)
                );
                const profSnap = await getDocs(profQuery);
                if (!profSnap.empty) {
                    sponsorFullName = profSnap.docs[0].data().name;
                }
            } catch (err) {
                console.error("Error fetching member profile:", err);
            }

            if (!sponsorFullName) {
                const activeMemberLog = accessItems.find(i => i.entryType !== 'Guest' && i.fullName);
                sponsorFullName = activeMemberLog?.fullName || localStorage.getItem("user") || cleanMemberEmail;
            }

            // Doc ID format: YYYY-MM-DD_guest_first_last
            const cleanGuestName = newGuestName.trim().toLowerCase().replace(/\s+/g, '_');
            const guestDocId = `${shiftDateStr}_guest_${cleanGuestName}`;
            const guestDocRef = doc(db, getLogsCollectionName(timestamp), guestDocId);

            const initialGuestEvent = {
                timestamp: Timestamp.fromDate(timestamp),
                method: "Self Check In",
                type: "Guest",
                status: "active",
                isVerified: false,
                isBypassed: false,
                verifier: cleanMemberEmail
            };

            await setDoc(guestDocRef, {
                fullName: newGuestName.trim(),
                entryType: "Guest",
                method: "Self Check In",
                signedInBy: cleanMemberEmail,
                sponsorName: sponsorFullName,
                rawPayload: cleanMemberEmail,
                scannedAt: Timestamp.fromDate(timestamp),
                lastScannedAt: Timestamp.fromDate(timestamp),
                status: "active",
                isVerified: false,
                isExpired: false,
                hasIncident: false,
                isFlagged: false,
                remove: false,
                noshow: false,
                scans: [initialGuestEvent]
            }, { merge: true });

            setNewGuestName("");
            const updated = await fetchGuests(email);
            setGuestItems(updated);

            const hasPending = updated.some(g => !g.isVerified || g.isFlagged || g.hasIncident);
            const allVerified = updated.length > 0 && updated.every(g => g.isVerified && !g.isFlagged && !g.hasIncident);
            setAccessItems(prev => prev.map(item =>
                item.rawPayload === email ? { ...item, guestCount: updated.length, hasPendingGuest: hasPending, allGuestsVerified: allVerified } : item
            ));
            toast.success("Guest added");
        } catch (e) {
            console.error("Add Guest Error:", e);
            toast.error("Error adding guest");
        } finally {
            setIsAddingGuest(false);
        }
    };

    const handleCancelGuest = async (guestId, email) => {
        if (!window.confirm("Cancel or mark this guest as removed?")) return;
        spinner(true);
        try {
            await updateDoc(doc(db, getLogsCollectionName(), guestId), {
                status: "removed",
                remove: true,
                noshow: true,
                cancelledAt: Timestamp.now(),
                removedAt: Timestamp.now()
            });
            const updated = await fetchGuests(email);
            setGuestItems(updated);

            const hasPending = updated.some(g => !g.isVerified || g.isFlagged || g.hasIncident);
            const allVerified = updated.length > 0 && updated.every(g => g.isVerified && !g.isFlagged && !g.hasIncident);
            setAccessItems(prev => prev.map(item =>
                item.rawPayload === email ? { ...item, guestCount: updated.length, hasPendingGuest: hasPending, allGuestsVerified: allVerified } : item
            ));
            toast.info("Guest removed and marked as No Show");
        } catch (e) {
            console.error(e);
            toast.error("Failed to update guest status");
        } finally {
            spinner(false);
        }
    };

    const formatDisplayValue = (str) => {
        if (!str) return "";
        if (str.includes('@')) return str.toLowerCase();
        return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const activeMemberEmail = memberEmail || localStorage.getItem("email") || "";

    return (
        <div className="access-vitals-container d-inline-flex align-items-center">
            {accessItems && Array.isArray(accessItems) && accessItems.map((x) => {
                const isBlockedByFlag = (x.hasIncident || x.isFlagged || x.isBanned);
                // Member takes precedence: Member can access button even if guest state changes
                const canAccess = x.status === "bypassed" || (!x.isExpired && !isBlockedByFlag);

                return (
                    <div 
                        key={x.id} 
                        className="d-inline-flex flex-column align-items-center gap-1 hoverX animated fadeInRight" 
                        onClick={() => {
                            if (canAccess) {
                                handleShowGuests(x.rawPayload || activeMemberEmail);
                            }
                        }}
                        style={{ cursor: canAccess ? 'pointer' : 'default' }}
                    >
                        {canAccess && (
                            <>
                                <span
                                    className={x.status === "bypassed" ? "badge rounded-pill bg-danger text-dark shadow-sm d-inline-flex align-items-center overflow-hidden p-0 pe-2" : "badge rounded-pill blBk text-dark shadow-sm d-inline-flex align-items-center overflow-hidden p-0 pe-2"}
                                    style={{ fontSize: '0.58rem', fontWeight: 'bold' }}>
                                    {x.isVerified ? (
                                        <span className="text-white d-inline-flex align-items-center">
                                            <span className="icon-circle me-1 d-inline-flex align-items-center justify-content-center bg-dark" style={{ width: '18px', height: '18px', borderRadius: '50%' }}>
                                                <FaCheck className="lgreen" title={`Verified by ${x.verifiedBy || 'Staff'}`} style={{ fontSize: '0.55rem' }} />
                                            </span>
                                            Verified
                                        </span>
                                    ) : (
                                        <span className="text-white d-inline-flex align-items-center">
                                            <span className="icon-circle me-1 d-inline-flex align-items-center justify-content-center bg-dark" style={{ width: '18px', height: '18px', borderRadius: '50%' }}>
                                                <FaKey className="text-white" style={{ fontSize: '0.55rem' }} />
                                            </span>
                                            Scan for Entry
                                        </span>
                                    )}
                                </span>

                                <span className="badge rounded-pill orBk shadow-sm d-inline-flex align-items-center overflow-hidden p-0 pe-2"
                                    style={{ fontSize: '0.58rem', fontWeight: 'bold' }}>
                                    <span className="icon-circle me-1 d-inline-flex align-items-center justify-content-center bg-dark" style={{ width: '18px', height: '18px', borderRadius: '50%' }}>
                                        {x.allGuestsVerified ? (
                                            <FaCheck className="lgreen" title="All Guests Verified" style={{ fontSize: '0.55rem' }} />
                                        ) : x.hasPendingGuest ? (
                                            <FaExclamationTriangle className="text-warning" title="Pending Guest Verification" style={{ fontSize: '0.55rem' }} />
                                        ) : x.guestCount > 0 ? (
                                            <FaUser style={{ fontSize: '0.55rem' }} />
                                        ) : (
                                            <FaPlus style={{ fontSize: '0.55rem' }} />
                                        )}
                                    </span>
                                    {x.guestCount > 0 ? `${x.guestCount} ${x.guestCount === 1 ? 'Guest' : 'Guests'}` : 'Guest'}
                                </span>
                            </>
                        )}

                        <span className="small text-white opacity-90 fw-bold" style={{ fontSize: '0.68rem' }}>
                            {x.hasIncident ? <span className="red">Incident &nbsp;</span> : (x.isExpired ? <span className="red">Expired &nbsp;</span> : "")}
                            {x.status === "bypassed" ? <span className="white"> - By Passed</span> : ""}
                            {!x.isExpired && !x.hasIncident && (
                                <span>{x.scannedAt?.toDate ? x.scannedAt.toDate().toLocaleDateString() : new Date().toLocaleDateString()}</span>
                            )}
                        </span>
                    </div>
                );
            })}

            <Modal show={showGuestModal} onHide={() => setShowGuestModal(false)} centered contentClassName="qr-modal-content bg-dark-profile" backdropClassName='bg-dark'>
                <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary)' }}></div>
                <Modal.Body className="p-4 text-white text-center">
                    <h2 className="fw-bold mb-1 text-center">Guest Management</h2>
                    <p className="qr-subtext small text-center mb-3 opacity-90">
                        Member/Sponsor: {formatDisplayValue(accessItems.find(i => i.entryType !== 'Guest')?.fullName || localStorage.getItem("user") || memberEmail)}
                    </p>

                    {activeMemberEmail && (
                        <div className="mb-4 text-center">
                            <div className="p-3 bg-white rounded-3 d-inline-block shadow-sm">
                                <QRCode
                                    value={activeMemberEmail}
                                    size={110}
                                    className=""
                                    bgColor="transparent"
                                    fgColor={"#0b0b0b"}
                                    level="M"
                                />
                            </div>
                            <div className="mt-2 orange" style={{ fontSize: '1.3rem' }}>
                                Flash QR Code to Bar Staff
                            </div>
                        </div>
                    )}

                    <div className="qr-guest-section mb-4 text-start">
                        <InputGroup className="mb-2">
                            <Form.Control
                                placeholder="Guest Name..."
                                className="qr-input"
                                value={newGuestName}
                                onChange={(e) => setNewGuestName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddGuest(accessItems[0]?.rawPayload || memberEmail)}
                            />
                            <button
                                className="qr-btn qr-btn-primary"
                                onClick={() => handleAddGuest(accessItems[0]?.rawPayload || memberEmail)}
                                disabled={isAddingGuest}
                            >
                                {isAddingGuest ? <Spinner size="sm" /> : "ADD"}
                            </button>
                        </InputGroup>
                    </div>

                    <div className="qr-card-list custom-scroll" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {guestItems.map((guest) => (
                            <div key={guest.id} className="qr-log-card py-2 px-3 d-flex justify-content-between align-items-center mb-2" style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '6px' }}>
                                <div className="d-flex align-items-center gap-2 text-start">
                                    {guest.isVerified ? (
                                        <FaCheck className="text-success" title={`Verified by ${guest.verifiedBy || 'Staff'}`} size={14} />
                                    ) : (
                                        <FaTimesCircle className="text-danger" title="Unverified Guest" size={14} />
                                    )}
                                    <div className="d-flex flex-column text-start">
                                        <span className="fw-bold small text-white">{formatDisplayValue(guest.fullName)}</span>
                                        <span className="text-white-50" style={{ fontSize: '0.68rem' }}>
                                            {guest.isVerified ? `Verified by ${guest.verifiedBy || 'Staff'}` : 'Pending Verification'}
                                        </span>
                                    </div>
                                </div>
                                {!guest.isVerified && (
                                    <button
                                        className="btn btn-sm text-danger fw-bold border-0 bg-transparent p-0"
                                        onClick={() => handleCancelGuest(guest.id, accessItems[0]?.rawPayload || memberEmail)}
                                    >
                                        REMOVE
                                    </button>
                                )}
                            </div>
                        ))}
                        {guestItems.length === 0 && (
                            <div className="text-center py-4 qr-subtext text-white-50">No active guests.</div>
                        )}
                    </div>
                    <button className="qr-btn qr-btn-warning w-100 mt-4" onClick={() => setShowGuestModal(false)}>Close</button>
                </Modal.Body>
            </Modal>
        </div>
    );
}